/**
 * Choisir un dossier racine dans OneDrive — l'explorateur de la création.
 *
 * **Il parcourt, il ne rapporte pas.** Là où le NavigateurOneDrive coche des
 * fichiers pour les importer dans une affaire *existante*, celui-ci n'a pas
 * d'affaire sous la main : il sert la toute première étape, avant même que
 * l'affaire naisse. On y descend l'arborescence, et l'on désigne un dossier.
 *
 * **Il n'a besoin d'aucune affaire.** Le parcours OneDrive est indexé sur le
 * compte lié, non sur l'affaire : `GET /auth/onedrive` pour les comptes,
 * `GET /api/v1/onedrive/{connexion}/enfants?chemin=…` pour le contenu — la
 * racine du drive quand le chemin est vide. C'est exactement ce que réclame un
 * choix de dossier fait *avant* la création.
 *
 * **On désigne un dossier, pas le dossier courant.** Chaque ligne-dossier
 * porte son « Choisir ce dossier » : le geste rapporte alors un `item_id` et un
 * `drive_id` vrais. Le fil d'Ariane, lui, ne porte que des chemins — s'en
 * servir pour choisir « là où je me tiens » donnerait une identité fragile au
 * renommage. On préfère la ligne, et son identité pleine.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import { api, ErreurApi } from "../../api/client";
import type { Connexion, DossierRacine, Element, Parcours } from "../../api/types";
import { Avis } from "../../composants/avis";
import { useModale } from "../../composants/clavier";
import { Chargement, Vide } from "../../composants/etats";
import { quand } from "../../composants/formats";

/** Où le portail nous ramène après le détour par Microsoft : ici même, pour que
 * l'accordéon se rouvre sur le volet OneDrive une fois le compte lié. */
function lienDeLiaison(): string {
  const ici = window.location.pathname + window.location.search;
  return `/auth/onedrive/link?suite=${encodeURIComponent(ici)}`;
}

export function SelecteurDossier({
  fermer,
  onChoisir,
}: {
  fermer: () => void;
  onChoisir: (dossier: DossierRacine) => void;
}) {
  const cadre = useModale(true, fermer);
  const [connexions, poserConnexions] = useState<Connexion[] | null>(null);
  const [connexion, poserConnexion] = useState<string>("");
  const [chemin, poserChemin] = useState<string>("");
  const [parcours, poserParcours] = useState<Parcours | null>(null);
  const [charge, poserCharge] = useState(true);
  const [va, poserVa] = useState<string | null>(null);
  const [erreur, poserErreur] = useState("");

  useEffect(() => {
    api
      .lire<{ donnees: Connexion[] }>("/auth/onedrive")
      .then((issue) => {
        const liste = issue.donnees ?? [];
        poserConnexions(liste);
        const vivante = liste.find((c) => c.utilisable);
        if (vivante) poserConnexion(vivante.connexion_id);
        // Sans compte utilisable, rien ne se chargera : on coupe le squelette
        // pour montrer tout de suite l'invite à lier un compte.
        else poserCharge(false);
      })
      .catch(() => {
        poserConnexions([]);
        poserCharge(false);
      });
  }, []);

  const parcourir = useCallback(
    async (versChemin: string) => {
      if (!connexion) return;
      poserVa(versChemin);
      poserErreur("");
      try {
        const issue = await api.lire<Parcours>(
          `/api/v1/onedrive/${connexion}/enfants?chemin=${encodeURIComponent(versChemin)}`,
        );
        poserParcours(issue);
        poserChemin(versChemin);
      } catch (exc) {
        poserErreur(
          exc instanceof ErreurApi
            ? exc.message
            : "Le dossier n'a pas pu être ouvert.",
        );
      } finally {
        poserCharge(false);
        poserVa(null);
      }
    },
    [connexion],
  );

  /** La page suivante du dossier courant, ajoutée à celle qu'on lit. */
  const encore = useCallback(async () => {
    if (!connexion || !parcours?.suite) return;
    poserVa(chemin);
    try {
      const issue = await api.lire<Parcours>(
        `/api/v1/onedrive/${connexion}/enfants?chemin=${encodeURIComponent(chemin)}`
        + `&curseur=${encodeURIComponent(parcours.suite)}`,
      );
      poserParcours((avant) =>
        avant ? { ...issue, elements: [...avant.elements, ...issue.elements] } : issue,
      );
    } catch (exc) {
      poserErreur(
        exc instanceof ErreurApi ? exc.message : "La suite n'a pas pu être lue.",
      );
    } finally {
      poserVa(null);
    }
  }, [connexion, chemin, parcours?.suite]);

  useEffect(() => {
    if (connexion) void parcourir("");
  }, [connexion, parcourir]);

  const elements = parcours?.elements ?? [];
  const dossiers = useMemo(() => elements.filter((e) => e.dossier), [elements]);
  const fichiers = useMemo(() => elements.filter((e) => !e.dossier), [elements]);

  const utilisables = connexions?.filter((c) => c.utilisable) ?? null;
  const aucuneUtilisable = utilisables !== null && utilisables.length === 0;
  const liees = connexions?.length ?? 0;

  function choisir(element: Element) {
    onChoisir({
      connexion_id: connexion,
      drive_id: element.drive_id,
      item_id: element.item_id,
      nom: element.nom,
      chemin: chemin ? `${chemin}/${element.nom}` : element.nom,
    });
  }

  return (
    <div className="voile" role="dialog" aria-modal="true"
         aria-label="Choisir le dossier racine">
      <div className="modale modale--picker" ref={cadre}>
        <div className="modale__tete">
          <div className="pile pile--serree" style={{ flex: 1, minWidth: 0 }}>
            <h2>Choisir le dossier racine</h2>
            {parcours && (
              <nav className="fil" aria-label="Fil d'Ariane">
                {parcours.fil.map((cran, rang) => (
                  <span key={cran.chemin || "racine"}>
                    {rang > 0 && <span aria-hidden="true"> / </span>}
                    <button type="button" onClick={() => void parcourir(cran.chemin)}>
                      {cran.nom}
                    </button>
                  </span>
                ))}
              </nav>
            )}
          </div>
          {utilisables && utilisables.length > 1 && (
            <select
              value={connexion}
              onChange={(e) => poserConnexion(e.target.value)}
              aria-label="Compte OneDrive"
            >
              {utilisables.map((c) => (
                <option key={c.connexion_id} value={c.connexion_id}>
                  {c.compte}
                </option>
              ))}
            </select>
          )}
        </div>

        {!aucuneUtilisable && !erreur && !charge && (
          <div className="picker__outils">
            <span className="discret">
              {dossiers.length} dossier(s)
              {fichiers.length > 0 && ` · ${fichiers.length} fichier(s)`}
            </span>
            <span style={{ flex: 1 }} />
            {va !== null && <span className="picker__travail">Chargement…</span>}
          </div>
        )}

        <div className="modale__corps">
          {aucuneUtilisable ? (
            <Vide
              titre={
                liees === 0
                  ? "Aucun compte OneDrive lié"
                  : "Votre compte OneDrive doit être reconnecté"
              }
              aide={
                liees === 0
                  ? "Liez votre compte pour parcourir vos dossiers et en choisir "
                    + "un pour l'affaire."
                  : `L'accès n'est plus valable (${
                      connexions?.map((c) => `${c.compte} — ${c.sante}`).join(", ")
                    }). Reconnectez-le pour continuer.`
              }
              action={
                <a className="bouton bouton--primaire" href={lienDeLiaison()}>
                  {liees === 0
                    ? "Connecter mon OneDrive"
                    : "Reconnecter mon OneDrive"}
                </a>
              }
            />
          ) : erreur ? (
            <div style={{ padding: "var(--mo-space-5)" }}>
              <Avis ton="erreur" cle={erreur}>{erreur}</Avis>
            </div>
          ) : charge ? (
            <div style={{ padding: "var(--mo-space-5)" }}>
              <Chargement lignes={5} />
            </div>
          ) : (
            <table className={`tableau${va !== null ? " tableau--attente" : ""}`}>
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Modifié</th>
                  <th style={{ width: 170 }} aria-label="Choisir" />
                </tr>
              </thead>
              <tbody>
                {dossiers.map((element) => {
                  const versChemin = chemin ? `${chemin}/${element.nom}` : element.nom;
                  const ouvre = va === versChemin;
                  return (
                    <tr
                      key={element.item_id}
                      className={`cliquable${ouvre ? " picker__ligne--ouvre" : ""}`}
                      onClick={() => void parcourir(versChemin)}
                    >
                      <td>
                        <span aria-hidden="true">📁</span> {element.nom}
                      </td>
                      <td className="discret nombre">{quand(element.modifie_le)}</td>
                      <td>
                        <button
                          type="button"
                          className="bouton bouton--discret selecteur__choisir"
                          onClick={(evenement) => {
                            evenement.stopPropagation();
                            choisir(element);
                          }}
                        >
                          Choisir ce dossier
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {/* Les fichiers ne se choisissent pas — ils ne sont là que pour
                    reconnaître le bon dossier. Estompés, sans action. */}
                {fichiers.map((element) => (
                  <tr key={element.item_id} className="selecteur__fichier">
                    <td>
                      <span aria-hidden="true">📄</span> {element.nom}
                    </td>
                    <td className="discret nombre">{quand(element.modifie_le)}</td>
                    <td />
                  </tr>
                ))}
                {elements.length === 0 && (
                  <tr>
                    <td colSpan={3}>
                      <Vide titre="Ce dossier est vide" />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {parcours?.suite && !charge && (
            <div className="picker__suite">
              <button type="button" className="bouton" disabled={va !== null}
                      onClick={() => void encore()}>
                {va !== null ? "Chargement…" : "Charger la suite du dossier"}
              </button>
            </div>
          )}
        </div>

        <div className="modale__pied">
          <span className="discret" style={{ flex: 1 }}>
            Ouvrez un dossier pour y descendre, puis « Choisir ce dossier ».
          </span>
          <button type="button" className="bouton" onClick={fermer}>
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
}
